import React from 'react';
import { I18n } from '@aws-amplify/core';

import { BeraResultCycleSummary } from '@/types/Bera';
import { useENS, useNotENS } from './_hooks';
import * as S from './styles';
import { Col } from 'antd';

interface ScoreTableProps {
	cycleSummary: BeraResultCycleSummary;
	isENS?: boolean;
	ens?: boolean;
}

export function ScoreTable({ cycleSummary, isENS = false, ens = false }: Readonly<ScoreTableProps>): JSX.Element {
	const getENSHighlight = useENS(cycleSummary);
	const getDefaultHighlight = useNotENS(cycleSummary);
	const resolveHighlight = isENS ? getENSHighlight : getDefaultHighlight;

	const subColumnTitles = [
		'Striking parts',
		'Hand activities',
		'Lifting',
		'Push/Pull One Hand',
		'Push/Pull Two Hand',
		'Push/Pull Cart',
		'Vibration',
		'Inadequate Clearance',
		'Contact Stress'
	];

	function getOverallFooterMessage() {
		if (!isENS) {
			return I18n.get('Total B.E.R.A.');
		}

		return I18n.get('Workstation');
	}

	function getOverallFooterColor() {
		if (!isENS) {
			return cycleSummary.overall_score.toFixed(1);
		}

		if (ens) {
			return I18n.get('ENS');
		}

		return I18n.get('No ENS');
	}

	function getBERAFooterCSS() {
		if (cycleSummary.overall_score > 15) {
			return { color: 'red', backgroundColor: '#FAEBEC' };
		}

		return { color: 'green', backgroundColor: '#e8f6eb' };
	}

	function getENSFooterCSS() {
		if (ens) {
			return { color: 'green', backgroundColor: '#e8f6eb' };
		}

		return { color: 'red', backgroundColor: '#FAEBEC' };
	}

	function getOverallFooterCSS() {
		if (!isENS) {
			return getBERAFooterCSS();
		}
		return getENSFooterCSS();
	}

	const { color, backgroundColor } = getOverallFooterCSS();


	return (
		<Col span={24} style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'center' }}>
			<S.TableContainer>
				<S.CustomTable>
					<thead>
						<tr>
							<S.EmptyHead rowSpan={2}>
								<div />
								<S.RiskFactor>{I18n.get('Risk Factors')}</S.RiskFactor>
								<S.TLID>{I18n.get('TLID')}</S.TLID>
							</S.EmptyHead>
							<th rowSpan={2}>
								<div>{I18n.get('Too High')}</div>
							</th>
							<th rowSpan={2}>
								<div>{I18n.get('Too Low')}</div>
							</th>
							<th rowSpan={2}>
								<div>{I18n.get('Too Far')}</div>
							</th>
							<th colSpan={2}>
								<div style={{ width: '5rem' }}>{I18n.get('Too Many')}</div>
							</th>
							<th colSpan={5}>
								<div style={{ width: '6rem' }}>{I18n.get('Too Much')}</div>
							</th>
							<th colSpan={2}>
								<div style={{ width: '5rem' }}>{I18n.get('Too Long')}</div>
							</th>
							<th rowSpan={2} colSpan={2}>
								<S.RPN>{I18n.get('Score RPN')}</S.RPN>
							</th>
						</tr>
						<tr>
							{subColumnTitles.map((title) => (
								<th key={title}>
									<div>
										<div>
											<span>{I18n.get(title)}</span>
										</div>
									</div>
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{cycleSummary.task_rpn_scores.map((task_scores, taskIndex) => (
							<tr key={cycleSummary.id + task_scores.id}>
								<td>{task_scores.task_name}</td>
								{task_scores.rpn_scores?.map((score, scoreIndex) => (
									<td
										key={`${task_scores.task_id}-${score.step_key_id}-${scoreIndex}`}
										style={{
											color: resolveHighlight({
												type: 'step',
												taskIndex,
												scoreIndex,
												value: score.task_rpn
											})
										}}
									>
										{score.task_rpn}
									</td>
								))}
								<td
									colSpan={2}
									style={{
										color: resolveHighlight({
											type: 'task-total',
											taskIndex,
											scoreIndex: -1,
											value: task_scores.rpn
										})
									}}
								>
									{task_scores.rpn}
								</td>
							</tr>
						))}
						<tr style={{ borderTop: '0.5rem solid #fff' }}>
							<td>{I18n.get('Weighted RPN')}</td>
							{cycleSummary?.step_keys_weighted_rpn?.map((score) => (
								<td key={`${cycleSummary.id}-${score.step_key_id}`}>{score.task_rpn.toFixed(1)}</td>
							))}
							<td colSpan={2} style={{ color: color, backgroundColor: backgroundColor }}>
								{getOverallFooterColor()}
								<div>{getOverallFooterMessage()}</div>
							</td>
						</tr>
					</tbody>
				</S.CustomTable>
			</S.TableContainer>
		</Col>
	);
}
